/**
 * Thin, explicit wrappers over libsodium. Everything here is synchronous and
 * allocation-explicit so the same code runs identically on Node, Bare and in
 * the browser (sodium-universal swaps in sodium-javascript/WASM there).
 */
import sodium from 'sodium-universal'
import b4a from 'b4a'

export const SEED_BYTES = 32
export const PUBLIC_KEY_BYTES = sodium.crypto_sign_PUBLICKEYBYTES // 32
export const SECRET_KEY_BYTES = sodium.crypto_sign_SECRETKEYBYTES // 64
export const SIGNATURE_BYTES = sodium.crypto_sign_BYTES // 64
export const BOX_PUBLIC_KEY_BYTES = sodium.crypto_box_PUBLICKEYBYTES // 32
export const BOX_SECRET_KEY_BYTES = sodium.crypto_box_SECRETKEYBYTES // 32
export const SEAL_BYTES = sodium.crypto_box_SEALBYTES // 48
export const SECRETBOX_KEY_BYTES = sodium.crypto_secretbox_KEYBYTES // 32
export const NONCE_BYTES = sodium.crypto_secretbox_NONCEBYTES // 24
export const MAC_BYTES = sodium.crypto_secretbox_MACBYTES // 16
export const HASH_BYTES = sodium.crypto_generichash_BYTES // 32

export function randomBytes (n = 32) {
  const buf = b4a.allocUnsafe(n)
  sodium.randombytes_buf(buf)
  return buf
}

export function hash (data, { key = null, bytes = HASH_BYTES } = {}) {
  const out = b4a.allocUnsafe(bytes)
  if (key) sodium.crypto_generichash(out, toBuffer(data), toBuffer(key))
  else sodium.crypto_generichash(out, toBuffer(data))
  return out
}

/** Domain-separated hash. Prevents cross-protocol collisions. */
export function domainHash (domain, ...parts) {
  const chunks = [b4a.from(domain, 'utf8')]
  for (const p of parts) {
    const buf = toBuffer(p)
    const len = b4a.allocUnsafe(4)
    b4a.writeUInt32LE(len, buf.byteLength, 0)
    chunks.push(len, buf)
  }
  return hash(b4a.concat(chunks))
}

/** Signing (Ed25519) */
export function signKeyPairFromSeed (seed) {
  const publicKey = b4a.allocUnsafe(PUBLIC_KEY_BYTES)
  const secretKey = b4a.allocUnsafe(SECRET_KEY_BYTES)
  sodium.crypto_sign_seed_keypair(publicKey, secretKey, toBuffer(seed))
  return { publicKey, secretKey }
}

export function signKeyPair () {
  return signKeyPairFromSeed(randomBytes(SEED_BYTES))
}

export function sign (message, secretKey) {
  const sig = b4a.allocUnsafe(SIGNATURE_BYTES)
  sodium.crypto_sign_detached(sig, toBuffer(message), toBuffer(secretKey))
  return sig
}

export function verify (message, signature, publicKey) {
  if (!signature || signature.byteLength !== SIGNATURE_BYTES) return false
  if (!publicKey || publicKey.byteLength !== PUBLIC_KEY_BYTES) return false
  return sodium.crypto_sign_verify_detached(
    toBuffer(signature), toBuffer(message), toBuffer(publicKey)
  )
}

/** Ed25519 -> X25519 so one identity key covers both signing and sealing. */
export function toBoxPublicKey (signPublicKey) {
  const out = b4a.allocUnsafe(BOX_PUBLIC_KEY_BYTES)
  sodium.crypto_sign_ed25519_pk_to_curve25519(out, toBuffer(signPublicKey))
  return out
}

export function toBoxSecretKey (signSecretKey) {
  const out = b4a.allocUnsafe(BOX_SECRET_KEY_BYTES)
  sodium.crypto_sign_ed25519_sk_to_curve25519(out, toBuffer(signSecretKey))
  return out
}

/** Anonymous sealed box — sender is not revealed by the ciphertext. */
export function seal (message, boxPublicKey) {
  const msg = toBuffer(message)
  const out = b4a.allocUnsafe(msg.byteLength + SEAL_BYTES)
  sodium.crypto_box_seal(out, msg, toBuffer(boxPublicKey))
  return out
}

export function sealOpen (ciphertext, boxPublicKey, boxSecretKey) {
  const ct = toBuffer(ciphertext)
  if (ct.byteLength < SEAL_BYTES) return null
  const out = b4a.allocUnsafe(ct.byteLength - SEAL_BYTES)
  const ok = sodium.crypto_box_seal_open(out, ct, toBuffer(boxPublicKey), toBuffer(boxSecretKey))
  return ok ? out : null
}

/** Symmetric AEAD (XSalsa20-Poly1305). Nonce is prepended to the ciphertext. */
export function encrypt (message, key) {
  const msg = toBuffer(message)
  const nonce = randomBytes(NONCE_BYTES)
  const box = b4a.allocUnsafe(msg.byteLength + MAC_BYTES)
  sodium.crypto_secretbox_easy(box, msg, nonce, toBuffer(key))
  return b4a.concat([nonce, box])
}

export function decrypt (ciphertext, key) {
  const ct = toBuffer(ciphertext)
  if (ct.byteLength < NONCE_BYTES + MAC_BYTES) return null
  const nonce = ct.subarray(0, NONCE_BYTES)
  const box = ct.subarray(NONCE_BYTES)
  const out = b4a.allocUnsafe(box.byteLength - MAC_BYTES)
  const ok = sodium.crypto_secretbox_open_easy(out, box, nonce, toBuffer(key))
  return ok ? out : null
}

/**
 * Deterministic subkey derivation. `context` is any string; only its first
 * 8 bytes are significant to libsodium, so we hash longer labels first.
 */
export function derive (masterKey, context, subkeyId = 0, bytes = SECRETBOX_KEY_BYTES) {
  const ctx = b4a.alloc(sodium.crypto_kdf_CONTEXTBYTES)
  const raw = b4a.from(context, 'utf8')
  // libsodium only reads 8 context bytes; hash longer labels so that
  // "pine/room/media" and "pine/room/thumbs" stay distinct.
  const label = raw.byteLength <= ctx.byteLength ? raw : hash(raw)
  b4a.copy(label, ctx, 0, 0, Math.min(ctx.byteLength, label.byteLength))
  const out = b4a.allocUnsafe(bytes)
  sodium.crypto_kdf_derive_from_key(out, subkeyId, ctx, toBuffer(masterKey))
  return out
}

/** Password-based key derivation for encrypted identity backups. */
export function passwordKey (password, salt, { moderate = true } = {}) {
  const out = b4a.allocUnsafe(SECRETBOX_KEY_BYTES)
  sodium.crypto_pwhash(
    out,
    b4a.from(password, 'utf8'),
    toBuffer(salt),
    moderate ? sodium.crypto_pwhash_OPSLIMIT_MODERATE : sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    moderate ? sodium.crypto_pwhash_MEMLIMIT_MODERATE : sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_DEFAULT
  )
  return out
}

/** Constant-time comparison. */
export function equal (a, b) {
  if (!a || !b || a.byteLength !== b.byteLength) return false
  return sodium.sodium_memcmp(toBuffer(a), toBuffer(b))
}

export function toBuffer (value) {
  if (b4a.isBuffer(value)) return value
  if (value instanceof Uint8Array) return value
  if (typeof value === 'string') return b4a.from(value, 'utf8')
  if (value instanceof ArrayBuffer) return new Uint8Array(value)
  throw new TypeError('Expected a buffer, Uint8Array or string')
}
